import { ReactNode } from "react";
import { Box } from "@mui/material";
import { useIsOnSmallScreen } from "@dotlegal/dotlegal-ui-components";
import { dotLegalLogoWithPayoff } from "@common/icons.tsx";
import DotLegalLogo from "../../../main/NavMenu/dotLegalLogo.svg?react";
const backgroundImageUrl = "https://7549814.fs1.hubspotusercontent-eu1.net/hubfs/7549814/website/body-bg.svg";

export interface IPublicPageContainerProps {
    children: ReactNode;
    centerVertically?: boolean;
    maxWidth?: number;
    /**
     * Makes the card fill the viewport height on a phone, so a stepped page can pin its heading to the
     * top and its buttons to the bottom within thumb reach instead of floating in the middle.
     */
    fillHeightOnSmallScreen?: boolean;
    logoWithoutPayoff?: boolean;
}

function PublicPageContainer({ children, centerVertically, maxWidth = 1200, fillHeightOnSmallScreen, logoWithoutPayoff }: IPublicPageContainerProps) {
    const isOnSmallScreen = useIsOnSmallScreen();
    const fillsHeight = isOnSmallScreen && fillHeightOnSmallScreen;

    return (
        <Box
            sx={{
                minHeight: "100vh",
                // On a phone 100vh is the viewport as it would be with the browser chrome hidden, so a
                // fill-height card puts its footer buttons behind the address and navigation bars. svh is
                // the always-visible height. It is preferred over dvh because dvh grows mid-scroll when the
                // address bar collapses, which would resize the card under the user's thumb.
                ...(fillsHeight && { "@supports (min-height: 100svh)": { minHeight: "100svh" } }),
                display: "flex",
                flexDirection: "column",
                backgroundImage: `url("${backgroundImageUrl}")`,
                backgroundColor: "#edf8fe",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 200px",
            }}
        >
            <Box
                sx={{
                    flexShrink: 0,
                    // The payoff logo would eat a third of the fold on a phone, so it shrinks rather than disappears.
                    "& svg": fillsHeight ? { height: "56px", width: "110px" } : { height: "130px", width: "200px" },
                    paddingLeft: fillsHeight ? "12px" : "20px",
                }}
            >
                <a href="https://dotlegal.com/en">{logoWithoutPayoff ? <DotLegalLogo /> : dotLegalLogoWithPayoff}</a>
            </Box>
            <Box
                sx={{
                    flexGrow: 1,
                    display: "flex",
                    justifyContent: "center",
                    // Stretching is what lets a fill-height page push its footer to the bottom of the card.
                    alignItems: centerVertically ? "center" : fillsHeight ? "stretch" : "flex-start",
                    paddingX: fillsHeight ? "12px" : isOnSmallScreen ? "16px" : "10%",
                    paddingBottom: fillsHeight ? "12px" : "5%",
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: maxWidth,
                        background: "white",
                        padding: fillsHeight ? "20px" : isOnSmallScreen ? "24px" : "48px",
                        borderRadius: fillsHeight ? "16px" : "25px",
                        boxShadow: fillsHeight ? "0 2px 20px rgba(81,105,142,.20)" : "0 0 200px rgba(81,105,142,.46)",
                        ...(fillsHeight ? { display: "flex", flexDirection: "column" } : { height: "fit-content" }),
                    }}
                >
                    {children}
                </Box>
            </Box>
        </Box>
    );
}

export default PublicPageContainer;
